import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

// Layout state machine for Browse / Chat + Context Panel modes.
// The context panel uses data-turbo-permanent to survive navigations.
// On reconnect, the existing chat is reloaded (not recreated).
export default class extends Controller {
  static targets = ["contextPanel"]

  connect() {
    if (this.hasContextPanelTarget && !this.contextPanelTarget.classList.contains("hidden")) {
      this.refreshPanelChat()
    }
  }

  // Action: Toggle context panel from the AI button in the page title bar.
  async toggleContextPanel(event) {
    event.preventDefault()
    if (!this.hasContextPanelTarget) return

    const isOpen = !this.contextPanelTarget.classList.contains("hidden")
    if (isOpen) {
      this.closeContextPanel()
      return
    }

    await this.#fetchChatAndOpen(event.currentTarget.dataset.latestChatUrl)
  }

  // Action: Start a new conversation from the context panel header button.
  async newContextChat(event) {
    event.preventDefault()
    if (!this.hasContextPanelTarget) return

    await this.#fetchChatAndOpen(event.currentTarget.dataset.createChatUrl, {
      method: "POST",
      headers: { "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content }
    })
  }

  // Action: Open a specific chat in context panel (from chat show page button)
  openContextPanel(event) {
    event.preventDefault()
    const chatUrl = event.currentTarget.dataset.chatUrl
    if (!chatUrl || !this.hasContextPanelTarget) return

    localStorage.setItem("app-mode-chat-url", chatUrl)
    this.loadPanelChat(chatUrl)
    this.contextPanelTarget.classList.remove("hidden")
  }

  // Action: Close context panel
  closeContextPanel() {
    if (!this.hasContextPanelTarget) return

    localStorage.removeItem("app-mode-chat-url")
    this.contextPanelTarget.classList.add("hidden")

    const frame = this.contextPanelTarget.querySelector("turbo-frame")
    if (frame) frame.removeAttribute("src")
  }

  // Action: Maximize panel → navigate to full chat view, keeping sidebar in browse mode
  maximizePanel() {
    const chatUrl = localStorage.getItem("app-mode-chat-url")
    this.closeContextPanel()
    if (chatUrl) {
      const url = new URL(chatUrl, window.location.origin)
      url.searchParams.set("sidebar", "browse")
      Turbo.visit(url.toString())
    }
  }

  // Private

  refreshPanelChat() {
    const chatUrl = localStorage.getItem("app-mode-chat-url")
    if (chatUrl) {
      this.loadPanelChat(chatUrl)
    }
  }

  async #fetchChatAndOpen(url, options = {}) {
    if (!url || !this.hasContextPanelTarget) return

    let chatUrl
    try {
      const response = await fetch(url, {
        ...options,
        headers: { Accept: "application/json", ...options.headers }
      })
      if (!response.ok) return
      const data = await response.json()
      chatUrl = data.url
      if (!chatUrl) return
    } catch {
      return
    }

    localStorage.setItem("app-mode-chat-url", chatUrl)
    this.loadPanelChat(chatUrl)
    this.contextPanelTarget.classList.remove("hidden")
  }

  loadPanelChat(url) {
    const frame = this.contextPanelTarget.querySelector("turbo-frame")
    if (!frame) return

    const panelUrl = new URL(url, window.location.origin)
    panelUrl.searchParams.set("context_panel", "true")
    const resolved = panelUrl.toString()
    const currentSrc = frame.src ? new URL(frame.src, window.location.origin).toString() : ""
    if (currentSrc !== resolved) {
      frame.src = resolved
    }
  }
}
